import React, { Suspense } from 'react';
import { StyleSheet, useWindowDimensions, View } from 'react-native';
import { ScrollView } from 'react-native-gesture-handler';
import SideMenu from './side-menu';
import { useDismissConvoPointerDown } from 'app/screens/chat/message-footer-context';
import {
  DASHBOARD_LAYOUT_CHILDREN_MAX_WIDTH,
  DASHBOARD_LAYOUT_CONTENT_MAX_WIDTH,
  DASHBOARD_LAYOUT_MAX_WIDTH,
} from './dashboard-layout-constants';
import { useUISize } from 'app/utils/web-helpers';
const MessageFooterOverlay = React.lazy(() =>
  import('app/screens/chat/message-footer').then((module) => ({ default: module.MessageFooterOverlay })),
);

interface SimpleVerticalScrollingLayoutProps {
  children: React.ReactNode;
  childrenMaxWidth?: number;
  withMessageFooter?: boolean;
  /**
   * Sometimes we need to skip this height for this case
   * modal take 90% of height but scrollview needs fixed height to be scrollable
   * In that case we skip fixed height and let parent handle it otherwise content will be show at the bottom because
   * deviceHeight * 0.9 \> 90% of parent height (modal)
   */
  skipFixedHeight?: boolean;
}

export function SimpleVerticalScrollingLayout({
  children,
  childrenMaxWidth = DASHBOARD_LAYOUT_CHILDREN_MAX_WIDTH,
  withMessageFooter,
  skipFixedHeight = false,
}: SimpleVerticalScrollingLayoutProps) {
  const { height: windowHeight } = useWindowDimensions();
  const { isLargeUI } = useUISize();
  const fixedHeight = windowHeight * 0.925;
  const dismissConvoPointerDown = useDismissConvoPointerDown();

  const scrollViewStyle = skipFixedHeight
    ? styles.scrollViewWithoutFixedHeight
    : isLargeUI
      ? [styles.scrollView, { height: fixedHeight }]
      : styles.scrollView;

  return (
    <View style={styles.bodyContainer}>
      <SideMenu />
      {withMessageFooter ? (
        <Suspense fallback={null}>
          <MessageFooterOverlay />
        </Suspense>
      ) : null}
      <ScrollView
        onPointerDown={dismissConvoPointerDown}
        nestedScrollEnabled
        showsVerticalScrollIndicator
        style={scrollViewStyle}
        contentContainerStyle={styles.scrollViewContent}
      >
        <View style={[styles.childrenContainer, { maxWidth: childrenMaxWidth }]}>{children}</View>
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  bodyContainer: {
    flex: 1,
    flexDirection: 'row',
    minHeight: 0,
    maxWidth: DASHBOARD_LAYOUT_MAX_WIDTH,
    marginHorizontal: 'auto',
    width: '100%',
  },
  scrollView: {
    flex: 1,
    minHeight: 0,
    minWidth: 0,
    maxWidth: DASHBOARD_LAYOUT_CONTENT_MAX_WIDTH,
  },
  scrollViewWithoutFixedHeight: {
    minHeight: 0,
    minWidth: 0,
    maxWidth: DASHBOARD_LAYOUT_CONTENT_MAX_WIDTH,
  },
  scrollViewContent: {
    flexGrow: 1,
  },
  childrenContainer: {
    flex: 1,
    width: '100%',
    maxWidth: DASHBOARD_LAYOUT_CHILDREN_MAX_WIDTH,
    alignSelf: 'center',
  },
});
